import React, { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  useProjectVendors,
  useProjectTags,
  useReports,
  useReportData,
  useImportVendors,
  useBulkTag,
  useDeleteVendor,
  useCreateProjectTag,
  useUpdateProjectTag,
  useDeleteProjectTag,
} from '../hooks/useProjectVendors';
import VendorCardView from './project/VendorCardView';

/**
 * 專案供應商管理元件
 * 管理專案內的供應商列表、匯入、標籤
 */
const ProjectVendorManagement = ({ projectId, project }) => {
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'card'
  const [showImportModal, setShowImportModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [filterTag, setFilterTag] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [vendorPage, setVendorPage] = useState(1);
  const vendorsPerPage = 20;
  const [showTagManageModal, setShowTagManageModal] = useState(false);

  // 取得資料
  const { data: vendorsData, isLoading: vendorsLoading, refetch: refetchVendors } = useProjectVendors(
    projectId,
    { status: filterStatus || undefined, tag: filterTag || undefined }
  );
  const { data: tagsData } = useProjectTags(projectId);

  const vendors = vendorsData?.vendors || [];
  const projectTags = tagsData?.tags || [];

  // 篩選供應商
  const filteredVendors = useMemo(() => {
    if (!searchTerm) return vendors;
    const term = searchTerm.toLowerCase();
    return vendors.filter(
      (v) =>
        v.name?.toLowerCase().includes(term) ||
        v.vendor_id?.toLowerCase().includes(term) ||
        v.tax_id?.includes(term)
    );
  }, [vendors, searchTerm]);

  // 分頁
  const totalVendorPages = Math.ceil(filteredVendors.length / vendorsPerPage);
  const paginatedVendors = filteredVendors.slice(
    (vendorPage - 1) * vendorsPerPage,
    vendorPage * vendorsPerPage
  );

  // 刪除供應商
  const deleteVendorMutation = useDeleteVendor({
    onSuccess: () => {
      toast.success('供應商已移除');
      refetchVendors(); // 直接呼叫 refetch
    },
    onError: (err) => toast.error(`移除失敗: ${err.message}`),
  });

  // 批量標籤
  const bulkTagMutation = useBulkTag({
    onSuccess: () => {
      toast.success('標籤已更新');
      setSelectedVendors([]);
      setShowTagModal(false);
    },
    onError: (err) => toast.error(`標籤更新失敗: ${err.message}`),
  });

  // 選取處理
  const handleSelectAll = () => {
    if (selectedVendors.length === filteredVendors.length) {
      setSelectedVendors([]);
    } else {
      setSelectedVendors(filteredVendors.map((v) => v.id));
    }
  };

  const handleSelectVendor = (vendorId) => {
    setSelectedVendors((prev) =>
      prev.includes(vendorId)
        ? prev.filter((id) => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  // 刪除確認
  const handleDeleteVendor = (vendor) => {
    if (window.confirm(`確定要從專案中移除「${vendor.name}」嗎？`)) {
      deleteVendorMutation.mutate({ projectId, vendorId: vendor.id });
    }
  };

  // 狀態標籤顏色
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: '待發送', bg: 'bg-gray-100', text: 'text-gray-700' },
      sent: { label: '已發送', bg: 'bg-blue-100', text: 'text-blue-700' },
      responded: { label: '已回覆', bg: 'bg-green-100', text: 'text-green-700' },
      expired: { label: '已過期', bg: 'bg-red-100', text: 'text-red-700' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* 標題列 */}
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-800">專案供應商</h2>
            {/* 視圖切換 */}
            <div className="flex bg-gray-200 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 text-sm rounded-md transition ${
                  viewMode === 'list'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                列表
              </button>
              <button
                onClick={() => setViewMode('card')}
                className={`px-3 py-1.5 text-sm rounded-md transition ${
                  viewMode === 'card'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                卡片
              </button>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowTagManageModal(true)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              管理標籤
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              從報表匯入
            </button>
          </div>
        </div>
      </div>

      {/* 卡片視圖 */}
      {viewMode === 'card' ? (
        <div className="p-6">
          <VendorCardView projectId={projectId} />
        </div>
      ) : (
        <>
          {/* 篩選列 */}
          <div className="px-6 py-3 border-b border-gray-100 flex flex-wrap gap-3 items-center">
            <input
              type="text"
              placeholder="搜尋供應商..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setVendorPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
            />
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setVendorPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">所有狀態</option>
              <option value="pending">待發送</option>
              <option value="sent">已發送</option>
              <option value="responded">已回覆</option>
              <option value="expired">已過期</option>
            </select>
            <select
              value={filterTag}
              onChange={(e) => { setFilterTag(e.target.value); setVendorPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">所有標籤</option>
              {projectTags.map((tag) => (
                <option key={tag.id} value={tag.name}>
                  {tag.name}
                </option>
              ))}
            </select>

            {selectedVendors.length > 0 && (
              <div className="ml-auto flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  已選 {selectedVendors.length} 項
                </span>
                <button
                  onClick={() => setShowTagModal(true)}
                  className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm"
                >
                  批量標籤
                </button>
              </div>
            )}
          </div>

          {/* 供應商列表 */}
          <div className="overflow-x-auto">
        {vendorsLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : filteredVendors.length === 0 ? (
          <div className="py-12 text-center">
            <div className="text-4xl mb-3">🏢</div>
            <p className="text-gray-600 mb-2">尚無供應商資料</p>
            <p className="text-sm text-gray-500">點擊「從報表匯入」開始新增供應商</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedVendors.length === filteredVendors.length}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  供應商
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Vendor ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  聯絡信箱
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  採購人員
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  事業部
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  標籤
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  狀態
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedVendors.map((vendor) => (
                <tr key={vendor.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedVendors.includes(vendor.id)}
                      onChange={() => handleSelectVendor(vendor.id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{vendor.name}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                    {vendor.vendor_id || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {vendor.contact_email || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {vendor.buyer || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {vendor.business_unit || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(vendor.tags || []).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(vendor.questionnaire_status)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDeleteVendor(vendor)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      移除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {/* 分頁 */}
        {!vendorsLoading && totalVendorPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              顯示 {(vendorPage - 1) * vendorsPerPage + 1}-{Math.min(vendorPage * vendorsPerPage, filteredVendors.length)} / {filteredVendors.length} 家
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setVendorPage(p => Math.max(1, p - 1))}
                disabled={vendorPage === 1}
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                上一頁
              </button>
              <span className="px-2 py-1 text-sm text-gray-600">{vendorPage} / {totalVendorPages}</span>
              <button
                onClick={() => setVendorPage(p => Math.min(totalVendorPages, p + 1))}
                disabled={vendorPage === totalVendorPages}
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                下一頁
              </button>
            </div>
          </div>
        )}
      </div>
        </>
      )}

      {/* 標籤管理 Modal */}
      {showTagManageModal && (
        <TagManageModal
          projectId={projectId}
          projectTags={projectTags}
          onClose={() => setShowTagManageModal(false)}
        />
      )}

      {/* 匯入 Modal */}
      {showImportModal && (
        <ImportFromReportModal
          projectId={projectId}
          project={project}
          projectTags={projectTags}
          onClose={() => setShowImportModal(false)}
          onImportSuccess={() => {
            refetchVendors();
            setShowImportModal(false);
          }}
        />
      )}

      {/* 批量標籤 Modal */}
      {showTagModal && (
        <BulkTagModal
          projectId={projectId}
          projectTags={projectTags}
          selectedVendors={selectedVendors}
          onClose={() => setShowTagModal(false)}
          onSuccess={() => setSelectedVendors([])}
        />
      )}
    </div>
  );
};

/**
 * 可排序的表頭
 */
const SortableHeader = ({ label, field, sortField, sortOrder, onSort, align = 'left' }) => (
  <th
    className={`px-4 py-2 text-${align} text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none`}
    onClick={() => onSort(field)}
  >
    <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
      {label}
      <span className="text-gray-400">
        {sortField === field ? (
          sortOrder === 'asc' ? '▲' : '▼'
        ) : '⬍'}
      </span>
    </div>
  </th>
);

/**
 * 可篩選的表頭
 */
const FilterableHeader = ({ label, field, options, selectedValues, onToggle, onClear, isOpen, onOpenChange }) => (
  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase relative">
    <button
      onClick={() => onOpenChange(!isOpen)}
      className={`flex items-center gap-1 hover:text-gray-700 ${selectedValues.length > 0 ? 'text-blue-600' : ''}`}
    >
      {label}
      {selectedValues.length > 0 && (
        <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-xs">
          {selectedValues.length}
        </span>
      )}
      <span>▼</span>
    </button>
    
    {isOpen && (
      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[150px] max-h-[200px] overflow-y-auto">
        <div className="p-2 border-b border-gray-100">
          <button
            onClick={onClear}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            清除篩選
          </button>
        </div>
        <div className="p-1">
          {options.map((option) => (
            <label
              key={option}
              className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer rounded"
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={() => onToggle(option)}
                className="rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm text-gray-700">{option}</span>
            </label>
          ))}
        </div>
      </div>
    )}
  </th>
);

/**
 * 快速建立標籤元件
 */
const CreateTagInline = ({ projectId, compact = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState('#3B82F6');
  const queryClient = useQueryClient();

  const createTagMutation = useCreateProjectTag({
    onSuccess: () => {
      toast.success('標籤已建立');
      setTagName('');
      setIsEditing(false);
      // 重新載入標籤
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tags'] });
    },
    onError: (err) => toast.error(`建立失敗: ${err.message}`),
  });

  const colors = ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#06B6D4', '#3B82F6', '#8B5CF6', '#EC4899'];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!tagName.trim()) return;
    createTagMutation.mutate({ projectId, name: tagName.trim(), color: tagColor });
  };

  if (!isEditing) {
    return (
      <button
        type="button"
        onClick={() => setIsEditing(true)}
        className={`${
          compact
            ? 'px-3 py-1.5 rounded-full text-sm border-2 border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600'
            : 'px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700'
        } transition`}
      >
        + 新增標籤
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
      <input
        type="text"
        value={tagName}
        onChange={(e) => setTagName(e.target.value)}
        placeholder="標籤名稱"
        className="w-24 px-2 py-1 border border-gray-200 rounded text-sm"
        autoFocus
      />
      <div className="flex flex-wrap gap-0.5">
        {colors.slice(0, 4).map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => setTagColor(color)}
            className={`w-5 h-5 rounded-full ${tagColor === color ? 'ring-2 ring-offset-1 ring-gray-400' : ''}`}
            style={{ backgroundColor: color }}
          />
        ))}
      </div>
      <button
        type="submit"
        disabled={!tagName.trim() || createTagMutation.isPending}
        className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
      >
        ✓
      </button>
      <button
        type="button"
        onClick={() => setIsEditing(false)}
        className="px-2 py-1 text-gray-500 hover:text-gray-700 text-xs"
      >
        ✕
      </button>
    </form>
  );
};

/**
 * 從報表匯入供應商 Modal
 */
const ImportFromReportModal = ({ projectId, project, projectTags, onClose, onImportSuccess }) => {
  const [step, setStep] = useState(1);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // 排序狀態
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  // 欄位篩選狀態
  const [columnFilters, setColumnFilters] = useState({});
  const [showFilterDropdown, setShowFilterDropdown] = useState(null);

  const { data: reportsData, isLoading: reportsLoading } = useReports();
  const { data: reportData, isLoading: dataLoading } = useReportData(
    selectedReport?.id,
    { limit: 1000 }
  );

  // 取得專案的權限標籤
  const projectAccessTags = useMemo(() => {
    return project?.access_tags || [];
  }, [project]);

  // 過濾報表：只顯示符合專案權限標籤的報表
  const reports = useMemo(() => {
    const allReports = reportsData?.reports || [];

    // 過濾條件：
    // 1. 報表必須已設定權限標籤（needs_tag_setup !== true）
    // 2. 報表的 access_tags 必須包含至少一個專案的權限標籤
    return allReports.filter(report => {
      // 排除未設定權限標籤的報表
      if (report.needs_tag_setup) return false;

      const reportTags = report.access_tags || [];

      // 如果專案沒有設定權限標籤，不顯示任何報表
      if (projectAccessTags.length === 0) return false;

      // 檢查報表是否有任一標籤符合專案的權限標籤
      return reportTags.some(tag => projectAccessTags.includes(tag));
    });
  }, [reportsData, projectAccessTags]);
  const vendors = reportData?.data || [];
  // 動態取得報表欄位
  const columns = reportData?.columns || selectedReport?.columns || [];

  // 識別 vendor_id 欄位（優先順序：vendor_id > 供應商代碼 > 統編 > 第一個欄位）
  const vendorIdColumn = useMemo(() => {
    const possibleColumns = ['vendor_id', '供應商代碼', 'Vendor ID', '統編', 'tax_id'];
    for (const col of possibleColumns) {
      if (columns.includes(col)) return col;
    }
    return columns[0] || 'vendor_id';
  }, [columns]);

  // 識別名稱欄位
  const nameColumn = useMemo(() => {
    const possibleColumns = ['name', '名稱', '供應商名稱', '公司名稱', 'Vendor Name'];
    for (const col of possibleColumns) {
      if (columns.includes(col)) return col;
    }
    return columns[1] || columns[0] || 'name';
  }, [columns]);

  // 取得每個欄位的唯一值（用於篩選下拉選單）
  const columnOptions = useMemo(() => {
    const result = {};
    columns.forEach(col => {
      const values = [...new Set(vendors.map(v => v[col]).filter(v => v !== null && v !== undefined && v !== ''))];
      result[col] = values.sort();
    });
    return result;
  }, [vendors, columns]);

  // 不做數字格式化的欄位（ID、代碼類）
  const noFormatColumns = useMemo(() => new Set([
    vendorIdColumn, 'vendor_id', '供應商代碼', 'Vendor ID', '統編', 'tax_id', '代碼', '編號',
  ]), [vendorIdColumn]);

  // 格式化儲存格值
  const formatCellValue = (value, column) => {
    if (value === null || value === undefined || value === '') return '-';
    // ID 類欄位不做數字格式化
    if (noFormatColumns.has(column)) return String(value);
    // 嘗試格式化數字
    const num = parseFloat(String(value).replace(/,/g, ''));
    if (!isNaN(num) && String(value).match(/^[\d,.-]+$/)) {
      return num.toLocaleString();
    }
    return String(value);
  };

  // 篩選和排序
  const filteredVendors = useMemo(() => {
    let result = vendors;

    // 文字搜尋（搜尋所有欄位）
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((v) =>
        columns.some(col => String(v[col] || '').toLowerCase().includes(term))
      );
    }

    // 動態欄位篩選
    Object.entries(columnFilters).forEach(([column, values]) => {
      if (values && values.length > 0) {
        result = result.filter(v => values.includes(v[column]));
      }
    });

    // 排序
    if (sortField) {
      result = [...result].sort((a, b) => {
        let aVal = a[sortField] || '';
        let bVal = b[sortField] || '';

        // 嘗試數值排序
        const aNum = parseFloat(String(aVal).replace(/,/g, ''));
        const bNum = parseFloat(String(bVal).replace(/,/g, ''));
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
        }

        // 字串排序
        if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [vendors, columns, searchTerm, columnFilters, sortField, sortOrder]);

  // 處理排序點擊
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // 處理欄位篩選
  const handleColumnFilter = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: prev[column].includes(value)
        ? prev[column].filter(v => v !== value)
        : [...prev[column], value]
    }));
  };

  // 清除欄位篩選
  const clearColumnFilter = (column) => {
    setColumnFilters(prev => ({ ...prev, [column]: [] }));
  };

  const importMutation = useImportVendors({
    onSuccess: (data) => {
      toast.success(`成功匯入 ${data.imported} 家供應商`);
      // 使用父元件的 callback 來 refetch 並關閉 Modal
      if (onImportSuccess) {
        onImportSuccess();
      } else {
        onClose();
      }
    },
    onError: (err) => toast.error(`匯入失敗: ${err.message}`),
  });

  const handleSelectReport = (report) => {
    setSelectedReport(report);
    setStep(2);
    setSelectedVendorIds([]);
    setColumnFilters({});
    setSortField('');
    setSortOrder('asc');
    setSearchTerm('');
  };

  const handleToggleVendor = (vendorId) => {
    setSelectedVendorIds((prev) =>
      prev.includes(vendorId)
        ? prev.filter((id) => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  const handleSelectAll = () => {
    const allIds = filteredVendors.map((v) => v[vendorIdColumn]);
    if (selectedVendorIds.length === allIds.length) {
      setSelectedVendorIds([]);
    } else {
      setSelectedVendorIds(allIds);
    }
  };

  const handleImport = () => {
    if (selectedVendorIds.length === 0) {
      toast.error('請選擇至少一家供應商');
      return;
    }
    if (selectedTags.length === 0) {
      toast.error('請選擇至少一個分類標籤');
      return;
    }
    importMutation.mutate({
      projectId,
      reportId: selectedReport.id,
      vendorIds: selectedVendorIds,
      tags: selectedTags,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">從報表匯入供應商</h2>
            <p className="text-sm text-gray-500">
              {step === 1 ? '步驟 1：選擇報表' : '步驟 2：選擇供應商'}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 ? (
            // Step 1: 選擇報表
            <div>
              {reportsLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                </div>
              ) : reports.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">📋</div>
                  <p className="text-gray-600 font-medium">沒有可匯入的報表</p>
                  <p className="text-sm text-gray-500 mt-2">
                    {projectAccessTags.length === 0
                      ? '此專案尚未設定權限標籤'
                      : '沒有符合專案權限標籤的報表'}
                  </p>
                  <p className="text-xs text-gray-400 mt-3">
                    報表的權限標籤必須包含專案的權限標籤才能匯入
                  </p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {reports.map((report) => (
                    <button
                      key={report.id}
                      onClick={() => handleSelectReport(report)}
                      className="w-full text-left p-4 border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{report.name}</div>
                          <div className="text-sm text-gray-500 mt-1">
                            {report.year} 年度 | {report.row_count?.toLocaleString()} 筆資料
                          </div>
                          {/* 顯示報表的權限標籤 */}
                          <div className="flex flex-wrap gap-1 mt-2">
                            {(report.access_tags || []).map(tag => (
                              <span
                                key={tag}
                                className={`px-2 py-0.5 text-xs rounded ${
                                  projectAccessTags.includes(tag)
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                        <svg className="w-5 h-5 text-gray-400 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Step 2: 選擇供應商
            <div>
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => setStep(1)}
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  返回選擇報表
                </button>
                <span className="text-gray-400">|</span>
                <span className="text-sm text-gray-600">{selectedReport?.name}</span>
              </div>

              {/* 標籤選擇區域（必填） */}
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-amber-600 text-lg">🏷️</span>
                    <span className="font-medium text-gray-800">分類標籤</span>
                    <span className="text-red-500 text-sm">* 必填</span>
                  </div>
                  {selectedTags.length > 0 && (
                    <span className="text-sm text-green-600">
                      ✓ 已選 {selectedTags.length} 個
                    </span>
                  )}
                </div>
                
                {projectTags.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-gray-600 mb-3">尚無專案標籤，請先建立</p>
                    <CreateTagInline projectId={projectId} />
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {projectTags.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() =>
                          setSelectedTags((prev) =>
                            prev.includes(tag.name)
                              ? prev.filter((t) => t !== tag.name)
                              : [...prev, tag.name]
                          )
                        }
                        className={`px-3 py-1.5 rounded-full text-sm border-2 transition ${
                          selectedTags.includes(tag.name)
                            ? 'bg-blue-100 border-blue-500 text-blue-700 font-medium'
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span
                          className="inline-block w-2 h-2 rounded-full mr-1.5"
                          style={{ backgroundColor: tag.color || '#6B7280' }}
                        />
                        {tag.name}
                      </button>
                    ))}
                    <CreateTagInline projectId={projectId} compact />
                  </div>
                )}
              </div>

              {/* 搜尋 */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="搜尋供應商..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              {dataLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                </div>
              ) : (
                <>
                  <div className="mb-3 flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedVendorIds.length === filteredVendors.length && filteredVendors.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-600">
                      全選 ({filteredVendors.length} 筆)
                    </span>
                    <span className="text-sm text-blue-600">
                      已選 {selectedVendorIds.length} 筆
                    </span>
                  </div>

                  <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-2 text-left w-10" />
                          {columns.map((column) => {
                            const hasFilterOptions = columnOptions[column]?.length > 0 && columnOptions[column].length <= 50;
                            return hasFilterOptions ? (
                              <FilterableHeader
                                key={column}
                                label={column}
                                field={column}
                                options={columnOptions[column] || []}
                                selectedValues={columnFilters[column] || []}
                                onToggle={(val) => handleColumnFilter(column, val)}
                                onClear={() => clearColumnFilter(column)}
                                isOpen={showFilterDropdown === column}
                                onOpenChange={(open) => setShowFilterDropdown(open ? column : null)}
                              />
                            ) : (
                              <SortableHeader
                                key={column}
                                label={column}
                                field={column}
                                sortField={sortField}
                                sortOrder={sortOrder}
                                onSort={handleSort}
                              />
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredVendors.map((vendor, idx) => {
                          const vendorId = vendor[vendorIdColumn];
                          return (
                            <tr
                              key={vendorId || idx}
                              className={`hover:bg-gray-50 cursor-pointer ${
                                selectedVendorIds.includes(vendorId) ? 'bg-blue-50' : ''
                              }`}
                              onClick={() => handleToggleVendor(vendorId)}
                            >
                              <td className="px-4 py-2">
                                <input
                                  type="checkbox"
                                  checked={selectedVendorIds.includes(vendorId)}
                                  onChange={() => {}}
                                  className="rounded border-gray-300"
                                />
                              </td>
                              {columns.map((column) => (
                                <td key={column} className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">
                                  {formatCellValue(vendor[column], column)}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 2 && (
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
            >
              取消
            </button>
            <button
              onClick={handleImport}
              disabled={selectedVendorIds.length === 0 || importMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {importMutation.isPending ? '匯入中...' : `匯入 ${selectedVendorIds.length} 家供應商`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * 批量標籤 Modal
 */
const BulkTagModal = ({ projectId, projectTags, selectedVendors, onClose, onSuccess }) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [action, setAction] = useState('add');
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');

  const bulkTagMutation = useBulkTag({
    onSuccess: () => {
      toast.success('標籤已更新');
      onSuccess();
      onClose();
    },
    onError: (err) => toast.error(`更新失敗: ${err.message}`),
  });

  const createTagMutation = useCreateProjectTag({
    onSuccess: () => {
      toast.success('標籤已建立');
      setNewTagName('');
    },
    onError: (err) => toast.error(`建立失敗: ${err.message}`),
  });

  const handleApply = () => {
    if (selectedTags.length === 0) {
      toast.error('請選擇至少一個標籤');
      return;
    }
    bulkTagMutation.mutate({
      projectId,
      vendorIds: selectedVendors,
      tags: selectedTags,
      action,
    });
  };

  const handleCreateTag = (e) => {
    e.preventDefault();
    if (!newTagName.trim()) return;
    createTagMutation.mutate({ projectId, name: newTagName.trim(), color: newTagColor });
  };

  const colors = ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#06B6D4', '#3B82F6', '#8B5CF6', '#EC4899'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">批量標籤操作</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            已選擇 <span className="font-medium">{selectedVendors.length}</span> 家供應商
          </p>

          {/* 操作類型 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">操作類型</label>
            <div className="flex gap-4">
              {[
                { value: 'add', label: '新增標籤' },
                { value: 'remove', label: '移除標籤' },
                { value: 'set', label: '設定標籤（取代現有）' },
              ].map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="action"
                    value={opt.value}
                    checked={action === opt.value}
                    onChange={(e) => setAction(e.target.value)}
                    className="text-blue-600"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* 選擇標籤 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">選擇標籤</label>
            <div className="flex flex-wrap gap-2">
              {projectTags.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() =>
                    setSelectedTags((prev) =>
                      prev.includes(tag.name)
                        ? prev.filter((t) => t !== tag.name)
                        : [...prev, tag.name]
                    )
                  }
                  className={`px-3 py-1.5 rounded-full text-sm border transition ${
                    selectedTags.includes(tag.name)
                      ? 'bg-blue-100 border-blue-500 text-blue-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-1.5"
                    style={{ backgroundColor: tag.color }}
                  />
                  {tag.name}
                </button>
              ))}
              {projectTags.length === 0 && (
                <p className="text-sm text-gray-500">尚無專案標籤，請先建立</p>
              )}
            </div>
          </div>

          {/* 建立新標籤 */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">建立新標籤</label>
            <form onSubmit={handleCreateTag} className="space-y-2">
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="標籤名稱"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <div className="flex flex-wrap gap-1.5">
                {colors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewTagColor(color)}
                    className={`w-6 h-6 rounded-full ${
                      newTagColor === color ? 'ring-2 ring-offset-1 ring-gray-400' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <button
                type="submit"
                disabled={!newTagName.trim() || createTagMutation.isPending}
                className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 text-sm"
              >
                建立
              </button>
            </form>
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
          >
            取消
          </button>
          <button
            onClick={handleApply}
            disabled={selectedTags.length === 0 || bulkTagMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
          >
            {bulkTagMutation.isPending ? '處理中...' : '套用'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * 標籤管理 Modal
 */
const TagManageModal = ({ projectId, projectTags, onClose }) => {
  const [editingTag, setEditingTag] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');
  const queryClient = useQueryClient();

  const colors = ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#06B6D4', '#3B82F6', '#8B5CF6', '#EC4899'];

  const createTagMutation = useCreateProjectTag({
    onSuccess: () => {
      toast.success('標籤已建立');
      setNewTagName('');
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tags'] });
    },
    onError: (err) => toast.error(`建立失敗: ${err.message}`),
  });

  const updateTagMutation = useUpdateProjectTag({
    onSuccess: () => {
      toast.success('標籤已更新');
      setEditingTag(null);
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tags'] });
    },
    onError: (err) => toast.error(`更新失敗: ${err.message}`),
  });

  const deleteTagMutation = useDeleteProjectTag({
    onSuccess: () => {
      toast.success('標籤已刪除');
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'tags'] });
    },
    onError: (err) => toast.error(`刪除失敗: ${err.message}`),
  });

  const handleStartEdit = (tag) => {
    setEditingTag(tag.id);
    setEditName(tag.name);
    setEditColor(tag.color);
  };

  const handleSaveEdit = () => {
    if (!editName.trim()) return;
    updateTagMutation.mutate({ projectId, tagId: editingTag, data: { name: editName.trim(), color: editColor } });
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!newTagName.trim()) return;
    createTagMutation.mutate({ projectId, name: newTagName.trim(), color: newTagColor });
  };

  const handleDelete = (tag) => {
    if (!window.confirm(`確定要刪除標籤「${tag.name}」嗎？`)) return;
    deleteTagMutation.mutate({ projectId, tagId: tag.id });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">管理標籤</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* 現有標籤列表 */}
          {projectTags.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">尚無標籤</p>
          ) : (
            <div className="space-y-2">
              {projectTags.map((tag) => (
                <div key={tag.id} className="p-3 bg-gray-50 rounded-lg">
                  {editingTag === tag.id ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500"
                        autoFocus
                      />
                      <div className="flex flex-wrap gap-1.5">
                        {colors.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setEditColor(c)}
                            className={`w-5 h-5 rounded-full ${editColor === c ? 'ring-2 ring-offset-1 ring-gray-400' : ''}`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                      <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={updateTagMutation.isPending}
                        className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                      >
                        儲存
                      </button>
                      <button
                        onClick={() => setEditingTag(null)}
                        className="px-2 py-1 text-gray-500 hover:text-gray-700 text-xs"
                      >
                        取消
                      </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <span
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="flex-1 text-sm font-medium text-gray-800">{tag.name}</span>
                      <button
                        onClick={() => handleStartEdit(tag)}
                        className="text-blue-600 hover:text-blue-800 text-xs"
                      >
                        編輯
                      </button>
                      <button
                        onClick={() => handleDelete(tag)}
                        disabled={deleteTagMutation.isPending}
                        className="text-red-600 hover:text-red-800 text-xs disabled:opacity-50"
                      >
                        刪除
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 新增標籤 */}
          <form onSubmit={handleCreate} className="space-y-2 pt-2 border-t border-gray-200">
            <input
              type="text"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              placeholder="新增標籤名稱..."
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
            />
            <div className="flex flex-wrap gap-1.5">
              {colors.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewTagColor(c)}
                  className={`w-5 h-5 rounded-full ${newTagColor === c ? 'ring-2 ring-offset-1 ring-gray-400' : ''}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <button
              type="submit"
              disabled={!newTagName.trim() || createTagMutation.isPending}
              className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50"
            >
              新增
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProjectVendorManagement;
